// src/auth/VerifyOtp.jsx
import React, { useEffect, useRef, useState } from "react";

import API_BASE_URL from "../config/api";
import { publish } from '../utils/eventBus';

const VerifyOtp = ({ navigate, onLogin }) => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error"); // "success" | "error"
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  

  const otpInputRef = useRef(null);

  // Prefill email from localStorage (set during register)
  useEffect(() => {
    const storedEmail = localStorage.getItem("pendingVerifyEmail");
    if (storedEmail) {
      setEmail(storedEmail);
      setMessage("Registration successful. Please verify your email.");
      setMessageType("success");
      // Optionally remove so banner doesn't persist after refresh:
      // localStorage.removeItem("pendingVerifyEmail");
    }
  }, []);

  // Autofocus OTP input when component mounts / email is set
  useEffect(() => {
    if (otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [email]);

  // -------------------------
  // Verify OTP (helper)
  // -------------------------
  const verifyOtpRequest = async () => {
    if (!email || !otp) {
      setMessage("Email and OTP are required");
      setMessageType("error");
      return false;
    }

    setMessage("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, otp }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }

      if (!res.ok) {
        setMessage(data.message || "OTP verification failed");
        setMessageType("error");
        return false;
      }

      // Save token if backend sends it
      if (data.token) {
        localStorage.setItem("authToken", data.token);
      }

      // If backend returned user info, treat as logged-in
      if (data.user) {
        localStorage.setItem('userName', data.user.username || '');
        if (data.user.role) localStorage.setItem('userRole', data.user.role);
        try { publish('auth:login', { email: data.user.email, username: data.user.username }); } catch (e) {}
        if (onLogin) onLogin(data.user.email, data.user.username);
      }

      setMessage("Email verified successfully. Redirecting...");
      setMessageType("success");

      // Clear stored email
      localStorage.removeItem("pendingVerifyEmail");

      // Redirect after short delay
      setTimeout(() => {
        navigate("products");
      }, 900);

      return true;
    } catch (err) {
      console.error("Verify OTP error:", err);
      setMessage("Something went wrong. Please try again.");
      setMessageType("error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    await verifyOtpRequest();
  };

  // -------------------------
  // Resend OTP
  // -------------------------
  const handleResendOtp = async () => {
    if (!email) {
      setMessage("Email is missing. Please register again.");
      setMessageType("error");
      return;
    }

    setMessage("");
    setResending(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        data = {};
      }

      if (!res.ok) {
        setMessage(data.message || "Failed to resend OTP");
        setMessageType("error");
        return;
      }

      setMessage("OTP resent successfully. Check your email.");
      setMessageType("success");

      // refocus input
      if (otpInputRef.current) otpInputRef.current.focus();
    } catch (err) {
      console.error("Resend OTP error:", err);
      setMessage("Unable to resend OTP. Try again later.");
      setMessageType("error");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h2 className="text-3xl font-bold text-center mb-6">Verify Email</h2>

      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md mx-auto">
        <form onSubmit={handleVerifyOtp} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="w-full border rounded-lg px-3 py-2 bg-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OTP
            </label>
            <input
              ref={otpInputRef}
              type="text"
              value={otp}
              onChange={(e) => {
                // allow only digits
                const val = e.target.value.replace(/\D/g, "");
                setOtp(val);
              }}
              placeholder="Enter 6-digit OTP"
              className="w-full border rounded-lg px-3 py-2"
              maxLength={6}
              disabled={loading || resending}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <button
          onClick={handleResendOtp}
          disabled={resending || loading}
          className="w-full mt-3 text-blue-600 hover:underline disabled:opacity-60"
        >
          {resending ? "Resending OTP..." : "Resend OTP"}
        </button>

        {message && (
          <p
            className={`mt-4 text-center text-sm ${
              messageType === "success" ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default VerifyOtp;
